import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCopy,
  faShoppingBasket,
  faPhone,
  faInfoCircle,
  faCheck,
  faTimes,
  faScroll,
  // faAward, // Unused
  faTriangleExclamation,
  faBolt,
  faClipboardCheck,
  // faChevronDown, // Icon for Show More - REMOVED
  // faChevronUp,   // Icon for Show Less - REMOVED
} from "@fortawesome/free-solid-svg-icons";
import MeatSelector from "./MeatSelector";
import CheeseSelector from "./CheeseSelector";
import DressingSelector from "./DressingSelector";
import ToppingSelector from "./ToppingSelector";
import useDatabase from "../../hooks/useDatabase";
import usePricing from "../../hooks/usePricing";
import { formatPrice } from "../../utils/pricingUtils"; // formatCurrency removed
import {
  generateOrderScript,
  generateSandwichName,
} from "../../utils/orderUtils";
import "./SandwichBuilder.css";
console.log('DEBUG: src/components/menu/SandwichBuilder.css imported');

const SandwichBuilder = () => {
  // Get database access
  const {
    isLoading,
    getAllBreads,
    getAllStyles,
    getAllToppings,
    
    getTemperatureOptions,
    getMeatById,
    // getCheeseById, // Unused
  } = useDatabase();

  // Get optimized pricing
  const { calculateSandwichPrice, isOptimized } = usePricing();

  // Load data from database
  const [breadOptions, setBreadOptions] = useState([]);
  const [styleOptions, setStyleOptions] = useState([]);
  const [toppingData, setToppingData] = useState([]);
  // const [dressingData, setDressingData] = useState([]); // Unused state
  // const [temperatureOptions, setTemperatureOptions] = useState([]); // Unused state

  // State for sandwich options
  const [primaryMeat, setPrimaryMeat] = useState("");
  const [primaryMeatVariant, setPrimaryMeatVariant] = useState("");
  const [primaryMeatTemperature, setPrimaryMeatTemperature] = useState(1); // will be set based on meat type
  const [secondaryMeat, setSecondaryMeat] = useState("");
  const [secondaryMeatVariant, setSecondaryMeatVariant] = useState("");
  const [secondaryMeatTemperature, setSecondaryMeatTemperature] = useState(1); // will be set based on meat type
  const [selectedCheeses, setSelectedCheeses] = useState([]);
  const [style, setStyle] = useState(1); // plain by default
  const [bread, setBread] = useState("1"); // Default to Rye bread (id 1)
  const [preparation, setPreparation] = useState(1); // regular by default
  const [selectedToppings, setSelectedToppings] = useState([]);
  const [selectedDressings, setSelectedDressings] = useState([]);
  const [totalPrice, setTotalPrice] = useState(0);
  const [showConsumptionWarning, setShowConsumptionWarning] = useState(false);
  // const [showAllToppings, setShowAllToppings] = useState(false); // State for topping visibility - REMOVED

  // Load static data on component mount
  useEffect(() => {
    try {
      if (!isLoading) {
        setBreadOptions(getAllBreads());
        setStyleOptions(getAllStyles());
        setToppingData(getAllToppings()); // Load all toppings initially
        // setDressingData(getAllDressings()); // Unused state
        // setTemperatureOptions(getTemperatureOptions()); // Unused state
      }
    } catch (error) {
      console.error("Error loading data:", error);
    }
  }, [
    isLoading,
    getAllBreads,
    getAllStyles,
    getAllToppings,
    // getAllDressings, // Corresponds to unused setDressingData
    // getTemperatureOptions, // Corresponds to unused setTemperatureOptions
  ]);

  // State for order script and buttons
  const [orderScript, setOrderScript] = useState("");
  const [isAddedToBasket, setIsAddedToBasket] = useState(false);
  const [isScriptCopied, setIsScriptCopied] = useState(false); // State for copy confirmation

  // State for basket
  const [basket, setBasket] = useState([]);
 
  // const toppings = selectedToppings; // Unused alias
 
  // Track extra meat quantity
  const [extraMeatQuantity, setExtraMeatQuantity] = useState(1);

  // Listen for extra meat quantity changes
  useEffect(() => {
    const handleExtraMeatQuantityChange = (event) => {
      setExtraMeatQuantity(event.detail.quantity);
    };

    document.addEventListener(
      "extraMeatQuantityChange",
      handleExtraMeatQuantityChange
    );

    return () => {
      document.removeEventListener(
        "extraMeatQuantityChange",
        handleExtraMeatQuantityChange
      );
    };
  }, []);

  // Set default temperature based on meat type when meat selection changes
  useEffect(() => {
    if (primaryMeat) {
      const meatId = parseInt(primaryMeat);
      // For corned beef (1), pastrami (2), and Hot Dog (21) default to hot (1)
      // For all other meats, default to cold (2)
      const defaultTemp = (meatId === 1 || meatId === 2 || meatId === 21) ? 1 : 2;
      setPrimaryMeatTemperature(defaultTemp);
      setSecondaryMeatTemperature(defaultTemp); // Set same temperature for both meats
    }
  }, [primaryMeat]);

  // Effect to handle Hot Dog bread selection
  useEffect(() => {
    const hotDogMeatId = 21; // ID for Hot Dog
    const hotDogBunId = "9"; // ID for Hot Dog Bun (as string, matching bread state)

    if (parseInt(primaryMeat) === hotDogMeatId) {
      setBread(hotDogBunId);
    }
    // If primaryMeat changes FROM hot dog to something else,
    // the user can then freely select bread again.
    // No explicit reset is done here, allowing previous non-hotdog bread to persist if desired,
    // or user can change it.
  }, [primaryMeat, setBread]); // setBread was missing as a dependency, added it.
 
  // Calculate price whenever sandwich options change
  useEffect(() => {
    if (isLoading || styleOptions.length === 0) return;

    // Create sandwich data object
    const sandwichData = {
      primaryMeat,
      primaryMeatVariant,
      primaryMeatTemperature,
      secondaryMeat,
      secondaryMeatVariant,
      secondaryMeatTemperature,
      selectedCheeses,
      style,
      bread,
      preparation,
      toppings: selectedToppings,
      dressings: selectedDressings,
    };

    // Calculate and update total price
    try {
      // Create pricing options with extra meat quantity
      const pricingOptions = {
        primaryMeat,
        primaryMeatVariant,
        secondaryMeat,
        secondaryMeatVariant,
        selectedCheeses,
        style,
        bread,
        preparation,
        toppings: selectedToppings,
        dressings: selectedDressings,
        extraMeatQuantity: extraMeatQuantity,
      };

      const currentStyleObj = styleOptions.find((s) => s.id === style);
      const result = calculateSandwichPrice(pricingOptions);

      setTotalPrice(result.totalPrice);

      // Generate order script using utility function
      const script = generateOrderScript(
        {
          ...sandwichData,
          styleObj: currentStyleObj,
          includedToppings: result.includedToppings,
          includedDressings: result.includedDressings,
        },
        result.totalPrice
      );

      setOrderScript(script);

      // Check if selected meat needs a consumption warning
      setShowConsumptionWarning(false);
      if (primaryMeat) {
        const meat = getMeatById(parseInt(primaryMeat));
        if (meat?.needs_warning) {
          setShowConsumptionWarning(true);
        }
      }

      if (secondaryMeat) {
        const meat = getMeatById(parseInt(secondaryMeat));
        if (meat?.needs_warning) {
          setShowConsumptionWarning(true);
        }
      }
    } catch (error) {
      console.error("Error calculating price:", error);
    }
  }, [
    isLoading,
    primaryMeat,
    primaryMeatVariant,
    primaryMeatTemperature,
    secondaryMeat,
    secondaryMeatVariant,
    secondaryMeatTemperature,
    selectedCheeses,
    style,
    bread,
    preparation,
    selectedToppings,
    selectedDressings,
    extraMeatQuantity, // Add dependency on extra meat quantity
    calculateSandwichPrice,
    styleOptions,
    getMeatById, // Added missing dependency
  ]);

  // Reset add-to-basket confirmation after 2 seconds
  useEffect(() => {
    if (isAddedToBasket) {
      const timer = setTimeout(() => {
        setIsAddedToBasket(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [isAddedToBasket]);

  // Reset copy confirmation after 2 seconds
  useEffect(() => {
    if (isScriptCopied) {
      const timer = setTimeout(() => {
        setIsScriptCopied(false);
      }, 1500); // Shorter duration for copy confirmation
      return () => clearTimeout(timer);
    }
  }, [isScriptCopied]);

  // Track if this is the first style selection (for initial default selections)
  const [isFirstStyleSelection, setIsFirstStyleSelection] = useState(true);

  // Handle style selection changes and default selections
  useEffect(() => {
    const isSpecialStyle = style === 2;
    const isSuperStyle = style === 3;
    const isStyleWithIncludedCheese = isSpecialStyle || isSuperStyle;
    const isPlainStyle = style === 1;

    if (isFirstStyleSelection) {
      // This is the initial setup run
      if (isStyleWithIncludedCheese) {
        setSelectedCheeses([
          { id: 1, quantity: "single", isIncluded: true, melted: false },
        ]);
        if (isSpecialStyle) {
          setSelectedToppings([3]); // Coleslaw ID
          setSelectedDressings([8]); // Russian Dressing ID
        } else if (isSuperStyle) {
          setSelectedToppings([1, 2]); // Lettuce and Tomato IDs
          setSelectedDressings([6]); // Mayo ID
        }
      } else if (isPlainStyle) {
        // For plain style on first load, ensure selections are empty
        setSelectedCheeses([]);
        setSelectedToppings([]);
        setSelectedDressings([]);
      }
      setIsFirstStyleSelection(false); // Mark initial setup as done
    } else {
      // This is a subsequent style change (not the first run)
      // Reset selections first to match the new style's defaults
      setSelectedCheeses([]); // Always reset cheese
      setSelectedToppings([]);
      setSelectedDressings([]);

      // Then apply new style's defaults if it's not plain
      if (isStyleWithIncludedCheese) {
        setSelectedCheeses([
          { id: 1, quantity: "single", isIncluded: true, melted: false },
        ]);
        if (isSpecialStyle) {
          setSelectedToppings([3]);
          setSelectedDressings([8]);
        } else if (isSuperStyle) {
          setSelectedToppings([1, 2]);
          setSelectedDressings([6]);
        }
      }
      // If the new style is plain, selections are already cleared above.
    }
  }, [style, isFirstStyleSelection]); // Run whenever style or isFirstStyleSelection changes

  // Handle bread selection
  const handleBreadChange = (e) => {
    setBread(e.target.value);
  };

  // Handle preparation selection
  const handlePreparationChange = (e) => {
    setPreparation(parseInt(e.target.value)); // Ensure value is number
  };

  // Handle meat temperature selection - applies to both meats
  const handlePrimaryTemperatureChange = (e) => {
    // Convert to a number to match our temperature IDs
    const temperature = parseInt(e.target.value);
    setPrimaryMeatTemperature(temperature);
    setSecondaryMeatTemperature(temperature); // Set same temperature for both meats
  };

  // Add to basket
  const addToBasket = () => {
    if (!primaryMeat || isLoading || styleOptions.length === 0) return;

    // Create sandwich data object
    const sandwichData = {
      primaryMeat,
      primaryMeatVariant,
      primaryMeatTemperature,
      secondaryMeat,
      secondaryMeatVariant,
      secondaryMeatTemperature,
      selectedCheeses,
      style,
      bread,
      preparation,
      toppings: selectedToppings,
      dressings: selectedDressings,
    };

    // Get style object
    const currentStyleObj = styleOptions.find((s) => s.id === style);

    // Use utility functions to generate sandwich name and script
    const sandwichName = generateSandwichName(
      {
        ...sandwichData,
        styleObj: currentStyleObj,
      },
      totalPrice
    );

    const newItem = {
      id: Date.now(),
      name: sandwichName,
      script: orderScript,
      data: sandwichData,
      price: totalPrice,
    };

    setBasket([...basket, newItem]);
    setIsAddedToBasket(true);

    // Reset selections (optional, based on desired UX)
    // setPrimaryMeat("");
    // setPrimaryMeatVariant("");
    // setSecondaryMeat("");
    // setSecondaryMeatVariant("");
    // setSelectedCheeses([]);
    // setStyle(1);
    // setBread("1");
    // setPreparation(1);
    // setSelectedToppings([]);
    // setSelectedDressings([]);
  };

  // Remove item from basket
  const removeFromBasket = (id) => {
    setBasket(basket.filter((item) => item.id !== id));
  };

  // Clear basket
  const clearBasket = () => {
    if (
      basket.length > 0 &&
      window.confirm("Are you sure you want to clear your entire order basket?")
    ) {
      setBasket([]);
    }
  };

  // Copy full order
  const copyFullOrder = () => {
    if (basket.length === 0) return;

    let orderText = `Star Deli Order - ${new Date().toLocaleDateString()}\n\n`;

    basket.forEach((item, index) => {
      orderText += `Item ${index + 1}: ${item.name}\n`;
      orderText += `${item.script}\n\n`;
    });

    orderText += `Total items: ${basket.length}\n`;
    orderText += `Call to order: 248-352-7377`;

    navigator.clipboard
      .writeText(orderText)
      .then(() => {
        // Show success message
        alert("Order copied to clipboard!");
      })
      .catch((err) => {
        console.error("Could not copy text: ", err);
      });
  };

  // Copy individual sandwich script
  const copyScript = () => {
    if (!orderScript || orderScript.includes("Select a meat")) return;

    navigator.clipboard
      .writeText(orderScript)
      .then(() => {
        setIsScriptCopied(true);
      })
      .catch((err) => {
        console.error("Could not copy script: ", err);
        alert("Failed to copy script.");
      });
  };

  // REMOVED toggleShowAllToppings function
  // const toggleShowAllToppings = () => {
  //   setShowAllToppings(!showAllToppings);
  // };

  // REMOVED NUM_TOPPINGS_VISIBLE constant
  // const NUM_TOPPINGS_VISIBLE = 6;

  return (
    <div className="basic-sandwich-info">
      <div className="basic-sandwich-steps">
        {/* Step 1: Choose Your Meat */}
        <div className="sandwich-step">
          <div className="step-number">1</div>
          <div className="step-content">
            <h3>Choose Your Meat</h3>

            <MeatSelector
              primaryMeat={primaryMeat}
              primaryMeatVariant={primaryMeatVariant}
              secondaryMeat={secondaryMeat}
              secondaryMeatVariant={secondaryMeatVariant}
              onSelectPrimary={setPrimaryMeat}
              onSelectPrimaryVariant={setPrimaryMeatVariant}
              onSelectSecondary={setSecondaryMeat}
              onSelectSecondaryVariant={setSecondaryMeatVariant}
            />

            {primaryMeat && (
              <div
                className="meat-temperature-options"
                style={{ marginTop: "1.5rem" }}
              >
                <h4>Meat Temperature</h4>
                {/* Meat temperature - applies to both meats */}
                <div className="temperature-selector">
                  <div className="meat-label">
                    {secondaryMeat
                      ? "Sandwich Temperature:"
                      : `${getMeatById(primaryMeat)?.name} Temperature:`}
                  </div>
                  <div className="temperature-options">
                    {getTemperatureOptions().map((option) => (
                      <label
                        key={`temperature-${option.id}`}
                        className="temperature-option"
                      >
                        <input
                          type="radio"
                          name="meat-temperature"
                          value={option.id}
                          checked={primaryMeatTemperature === option.id}
                          onChange={handlePrimaryTemperatureChange}
                        />
                        <span>{option.name}</span>
                      </label>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Step 2: Select Your Style */}
        <div className={`sandwich-step ${!primaryMeat ? "disabled" : ""}`}>
          <div className="step-number">2</div>
          <div className="step-content">
            <h3>Select Your Style</h3>
            <div className="style-selector">
              <div className="style-options-radio">
                {styleOptions.map((styleOption) => (
                  <label key={styleOption.id} className="style-radio-option">
                    <input
                      type="radio"
                      name="style"
                      value={styleOption.id}
                      checked={style === styleOption.id}
                      onChange={() => setStyle(styleOption.id)}
                    />
                    <div
                      className={`style-content ${
                        styleOption.id === 2 ? "highlight-style" : ""
                      }`}
                    >
                      <h4>
                        {styleOption.name}
                        {styleOption.price_adjustment > 0 && (
                          <span className="upgrade-price">
                            +${styleOption.price_adjustment.toFixed(2)}
                          </span>
                        )}
                      </h4>
                      <span className="included">
                        {styleOption.description}
                      </span>
                    </div>
                  </label>
                ))}
              </div>

              {(style === 2 || style === 3) && (
                <div className="style-pricing-note">
                  <p>
                    <strong>Pricing note:</strong> If you remove any included
                    toppings from your selected style, your price will be
                    reduced. For example, removing coleslaw saves $1.50,
                    removing tomato saves $1.50.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Step 3: Choose Your Cheese */}
        <div className={`sandwich-step ${!primaryMeat ? "disabled" : ""}`}>
          <div className="step-number">3</div>
          <div className="step-content">
            <h3>Choose Your Cheese</h3>
            <p className="step-description">
              {style === 2 || style === 3
                ? "Swiss cheese included with your style. Select another cheese or add extra."
                : style === 4
                ? "New Yorker style does not include cheese. Add cheese for $1.50. Double or triple portions available."
                : "Add cheese for $1.50. Double or triple portions available."}
            </p>

            <CheeseSelector
              selectedCheeses={selectedCheeses}
              onUpdateCheeses={setSelectedCheeses}
              includedCheese={style === 2 || style === 3 ? 1 : null} // 1 is Swiss cheese ID, New Yorker (4) has no included cheese
              isCold={primaryMeatTemperature === 2} // Pass if cold is selected to disable melting
            />
          </div>
        </div>

        {/* Step 4: Choose Your Bread */}
        <div className={`sandwich-step ${!primaryMeat ? "disabled" : ""}`}>
          <div className="step-number">4</div>
          <div className="step-content">
            <h3>Choose Your Bread</h3>
            <div className="custom-dropdown">
              <select
                id="bread-dropdown"
                className="meat-dropdown"
                value={bread}
                onChange={handleBreadChange}
                disabled={parseInt(primaryMeat) === 21} // Disable if Hot Dog (ID 21) is selected
              >
                <option value="" disabled>
                  -- Select bread type --
                </option>
                {parseInt(primaryMeat) === 21
                  ? breadOptions
                      .filter((option) => option.id === 9) // Show only Hot Dog Bun
                      .map((option) => (
                        <option key={option.id} value={option.id}>
                          {option.label}
                        </option>
                      ))
                  : breadOptions.map((option) => ( // Show all breads otherwise
                      <option key={option.id} value={option.id}>
                        {option.label}
                        {option.price_adjustment > 0
                          ? ` (+$${option.price_adjustment.toFixed(2)})`
                          : ""}
                      </option>
                    ))}
              </select>
              <div className="dropdown-arrow">
                <FontAwesomeIcon icon={faInfoCircle} />
              </div>
            </div>

            <div className="bread-preparation" style={{ marginTop: "1.5rem" }}>
              <h4>Bread Preparation</h4>
              <div className="preparation-options">
                <label className="preparation-option">
                  <input
                    type="radio"
                    name="preparation"
                    value={1}
                    checked={preparation === 1}
                    onChange={handlePreparationChange}
                  />
                  <span>Regular</span>
                </label>
                <label className="preparation-option">
                  <input
                    type="radio"
                    name="preparation"
                    value={2}
                    checked={preparation === 2}
                    onChange={handlePreparationChange}
                  />
                  <span>Toasted (+$0.10)</span>
                </label>
                <label className="preparation-option">
                  <input
                    type="radio"
                    name="preparation"
                    value={3}
                    checked={preparation === 3}
                    onChange={handlePreparationChange}
                  />
                  <span>Grilled (+$0.10)</span>
                </label>
              </div>
            </div>

            <div className="topping-note">
              <FontAwesomeIcon icon={faInfoCircle} /> You may also bring your
              own bread or bagel.
            </div>
          </div>
        </div>

        {/* Step 5: Add Dressings */}
        <div className={`sandwich-step ${!primaryMeat ? "disabled" : ""}`}>
          <div className="step-number">5</div>
          <div className="step-content">
            <h3>Add Dressings</h3>
            <p className="step-description">
              All dressings are included at no additional cost
            </p>

            <DressingSelector
              selectedDressings={selectedDressings}
              onUpdateDressings={setSelectedDressings}
              style={style}
              includedDressings={
                style === 2 ? [8] : style === 3 ? [6] : []
              } /* Special: Russian, Super: Mayo */
            />
          </div>
        </div>

        {/* Step 6: Add Toppings */}
        <div className={`sandwich-step ${!primaryMeat ? "disabled" : ""}`}>
          <div className="step-number">6</div>
          <div className="step-content">
            <h3>
              Add Toppings
              <span className="topping-price">Various prices</span>
            </h3>
            <p className="step-description">Select from our premium toppings</p>

            <ToppingSelector
              allToppings={toppingData} // Pass full list
              selectedToppings={selectedToppings}
              onUpdateToppings={setSelectedToppings}
              includedToppings={
                style === 2 ? [3] : style === 3 ? [1, 2] : []
              } /* Special: Coleslaw, Super: Lettuce, Tomato */
            />
            {/* REMOVED Show More/Less Button */}
          </div>
        </div>
      </div>

      {/* Price Display */}
      <div className="price-display order-total">
        <h3>
          Price Estimate: ${formatPrice(totalPrice)}
          {isOptimized && (
            <span
              className="optimization-badge"
              title="Using WebAssembly optimization"
            >
              <FontAwesomeIcon
                icon={faBolt}
                style={{ marginLeft: "8px", color: "#4CAF50" }}
              />
            </span>
          )}
        </h3>
        <p className="price-note">Price estimate before sales tax</p>
      </div>

      {/* Consumption Warning */}
      {showConsumptionWarning && (
        <div
          className="food-safety-warning"
          style={{
            backgroundColor: "#fff3cd",
            color: "#664d03",
            borderRadius: "5px",
            padding: "10px 15px",
            margin: "15px 0",
            border: "1px solid #ffecb5",
          }}
        >
          <h4
            style={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
              marginBottom: "5px",
            }}
          >
            <FontAwesomeIcon icon={faTriangleExclamation} /> Food Safety Notice
          </h4>
          <p style={{ fontSize: "14px", margin: "0" }}>
            NOTICE: Indicated items are served raw, undercooked, or cooked to
            order. Consuming raw or undercooked meats, poultry, seafood,
            shellfish, or eggs may increase your risk of foodborne illness,
            especially if you have certain medical conditions.
          </p>
        </div>
      )}

      {/* Order Script Section */}
      <div className="order-script-section">
        <h3>
          <FontAwesomeIcon icon={faScroll} /> Your Order Script
        </h3>
        <p className="script-instructions">
          Use this script when calling in your order:
        </p>

        <div className="order-script-box">
          <div id="order-script-content" className="order-script-content">
            {primaryMeat ? (
              orderScript
            ) : (
              <p className="script-placeholder">
                Select a meat to begin building your sandwich order.
              </p>
            )}
          </div>
          {/* Add Copy Button */}
          {primaryMeat && (
            <button
              className="copy-script-button"
              title="Copy script to clipboard"
              onClick={copyScript}
            >
              <FontAwesomeIcon
                icon={isScriptCopied ? faClipboardCheck : faCopy}
              />
              {isScriptCopied ? " Copied!" : ""}
            </button>
          )}
        </div>

        <div className="order-actions">
          <button
            id="add-to-basket-btn"
            className="btn secondary"
            disabled={!primaryMeat}
            onClick={addToBasket}
            style={{
              color: "#1A2530",
              fontWeight: "600",
            }} /* Ensure text is dark and readable */
          >
            <FontAwesomeIcon
              icon={isAddedToBasket ? faCheck : faShoppingBasket}
            />
            {isAddedToBasket ? "Added to Basket" : "Add to Order Basket"}
          </button>
          <a href="tel:248-352-7377" className="btn primary">
            <FontAwesomeIcon icon={faPhone} /> Call to Order
          </a>
        </div>
      </div>

      {/* Order Basket */}
      <div
        id="order-basket"
        className="order-basket"
        style={{ display: basket.length > 0 ? "block" : "none" }}
      >
        <h3>
          <FontAwesomeIcon icon={faShoppingBasket} /> Your Order Basket
        </h3>
        <div id="basket-items" className="basket-items">
          {basket.length === 0 ? (
            <div className="basket-empty">Your order basket is empty.</div>
          ) : (
            basket.map((item) => (
              <div key={item.id} className="basket-item">
                <h4 className="basket-item-title">{item.name}</h4>
                <div className="basket-item-script">{item.script}</div>
                <button
                  className="remove-item-btn"
                  onClick={() => removeFromBasket(item.id)}
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ))
          )}
        </div>
        <div className="basket-summary">
          <p>
            Total items: <span id="basket-count">{basket.length}</span>
          </p>
          <div className="basket-actions">
            <button
              id="clear-basket-btn"
              className="btn outline"
              disabled={basket.length === 0}
              onClick={clearBasket}
              style={{ color: "#2B3A4A" }} /* Ensure readable text color */
            >
              <FontAwesomeIcon icon={faTimes} /> Clear Basket
            </button>
            <button
              id="copy-full-order-btn"
              className="btn secondary"
              disabled={basket.length === 0}
              onClick={copyFullOrder}
              style={{
                color: "#1A2530",
                fontWeight: "600",
              }} /* Ensure text is dark and readable */
            >
              <FontAwesomeIcon icon={faCopy} /> Copy Full Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SandwichBuilder;
